import {
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  TextFieldProps,
  useTheme,
} from '@mui/material';
import isNil from 'lodash/isNil';
import { PropsWithChildren, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import RHFNumberField, { RHFNumberFieldProps } from '@/components/hook-form/RHFNumberField';
import { mergeSx } from '@/utils/cssStyles';

type Props = {
  /**
   * Props applied to the Select element.
   */
  SelectProps?: SelectProps;
  /**
   * Name of the select field (as per react-hook-form)
   */
  selectName: string;
  /**
   * Name of the input field (as per react-hook-form)
   */
  inputName: string;
  /**
   * Whether to disable the select field but not the input field.
   */
  selectDisabled?: boolean;
  helperText?: string;
  /**
   * If true an empty option will be included in the select with an empty string as value. (MUI requirement for a select without a value)
   * This option should only be set if a nullable value is a valid user input on the form. It does not make sense to provide a nullable input on a required form field.
   * Make sure to handle this null value both in your submit function and form schema validation properly via a check for an empty string with `.when()` Yup modifier.
   */
  includeEmptyOption?: boolean;
  /**
   * A node to be rendered additionally before the select control.
   */
  endAdornmentPrePosition?: ReactNode;
} & Omit<RHFNumberFieldProps, 'name'>;

export default function RHFInputWithSelect({
  selectName,
  inputName,
  children,
  SelectProps,
  InputProps,
  selectDisabled,
  helperText,
  includeEmptyOption = false,
  endAdornmentPrePosition,
  ...extendNumberFieldProps
}: PropsWithChildren<Props> & Pick<TextFieldProps, 'InputProps'> & Pick<TextFieldProps, 'InputLabelProps'>) {
  const { control } = useFormContext();
  const theme = useTheme();
  const isNumberFieldDisabled = !isNil(extendNumberFieldProps.state) && extendNumberFieldProps.state !== 'enabled';

  return (
    <RHFNumberField
      helperText={helperText}
      {...extendNumberFieldProps}
      sx={mergeSx(
        {
          '& > .MuiInputBase-root': {
            px: 1,
            py: 0,
          },
        },
        extendNumberFieldProps.sx,
      )}
      name={inputName}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {endAdornmentPrePosition}
            <InputAdornment position="end">
              <Controller
                name={selectName}
                control={control}
                render={({ field: { ref, ...field }, fieldState }) => {
                  const onSelectChange = (event: SelectChangeEvent<unknown>, child: ReactNode) => {
                    const value = event.target.value;
                    field.onChange(value);
                    SelectProps?.onChange?.(event, child);
                  };

                  return (
                    <FormControl error={Boolean(fieldState.error)}>
                      <Select
                        {...SelectProps}
                        {...field}
                        name={selectName}
                        disabled={isNumberFieldDisabled || selectDisabled}
                        inputRef={ref}
                        onChange={onSelectChange}
                        size="small"
                        sx={mergeSx(
                          {
                            backgroundColor: theme.palette.grey[500_8],
                            '&:hover':
                              !isNumberFieldDisabled && !selectDisabled
                                ? {
                                    backgroundColor: theme.palette.grey[500_24],
                                  }
                                : undefined,
                            '&& .MuiSelect-select': {
                              textOverflow: 'unset',
                            },
                            // Increasing specificity by repeating the class names
                            '&& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.grey[500_24], // Normal border color
                            },
                            '&&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.grey[500], // Custom color when the Select is focused
                            },
                          },
                          SelectProps?.sx,
                        )}
                        MenuProps={{
                          disablePortal: true,
                          ...SelectProps?.MenuProps,
                        }}
                      >
                        {includeEmptyOption ? <MenuItem value="" style={{ height: 36 }} /> : null}
                        {children}
                      </Select>
                    </FormControl>
                  );
                }}
              />
            </InputAdornment>
          </>
        ),
      }}
    />
  );
}
